import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import moment from 'moment';
import isNil from 'ramda/src/isNil';
import { DayPicker } from 'react-day-picker';
import { MAXIMUM_DAYS } from 'constants/TimeframePresets';
import MomentType from 'types/commons/MomentType';
import { enGB } from 'date-fns/locale';
import Tooltip from 'components/other/Tooltip';

// Scheduled check dot styles - positioned in top right corner of the day cell
const scheduledDotStyles = {
    weekly: {
        position: 'absolute',
        top: '1px',
        right: '1px',
        width: '5px',
        height: '5px',
        background: '#de3321',
        borderRadius: '50%',
        pointerEvents: 'none',
    },
    daily: {
        position: 'absolute',
        top: '1px',
        right: '5px',
        width: '5px',
        height: '5px',
        background: '#f59e0b',
        borderRadius: '50%',
        pointerEvents: 'none',
    },
};

const DateRangePicker = ({ from, to, start, onChange, scheduledWeeklyDates, scheduledDailyDates, showScheduledDots }) => {
    const validateDate = useCallback((date) => {
        if (moment.isMoment(date) && date.isValid()) {
            return date.toDate();
        }
        return null;
    }, []);

    const [range, setRange] = useState(() => ({
        from: validateDate(from),
        to: validateDate(to),
    }));

    useEffect(() => {
        setRange({
            from: validateDate(from),
            to: validateDate(to),
        });
    }, [from, to, validateDate]);

    const getFirstAvailableMonth = useCallback(() => {
        if (!isNil(start)) {
            return start.startOf('day').toDate();
        }
        return moment().subtract(MAXIMUM_DAYS, 'days').startOf('day').toDate();
    }, [start]);

    const getEndMonth = useCallback(() => {
        // Allow navigation to next month to see scheduled dots
        return moment().add(1, 'month').endOf('month').toDate();
    }, []);

    const getPreviousMonth = useCallback(() => {
        return moment().subtract(1, 'month').startOf('month').toDate();
    }, []);

    const getModifiers = useCallback(() => {
        let before = null;
        const s = !isNil(start) ? start.startOf('day') : null;
        if (!isNil(s)) {
            const maximumDay = moment().subtract(MAXIMUM_DAYS, 'days').startOf('day');
            before = s.isBefore(maximumDay) ? maximumDay.toDate() : s.toDate();
        }
        return {
            disabled: [{ before }, { after: moment().endOf('day').toDate() }],
            from: range.from,
            selected: { from: range.from, to: range.to },
            to: range.to,
        };
    }, [start, range]);

    const handleDayClick = useCallback(
        (day, { disabled = false }) => {
            const { from: stateFrom, to: stateTo } = range;
            if (!disabled) {
                if (stateFrom && day < stateFrom) {
                    setRange({ from: day, to: null });
                    onChange({ from: moment(day), to: null });
                } else if (stateFrom && !stateTo) {
                    setRange({ ...range, to: day });
                    onChange({ from: moment(stateFrom), to: moment(day) });
                } else if (stateFrom && stateTo) {
                    setRange({ from: day, to: null });
                    onChange({ from: moment(day), to: null });
                }
            }
        },
        [range, onChange]
    );

    // Convert scheduled dates arrays to Sets for O(1) lookup
    const weeklyDatesSet = useMemo(
        () => new Set(scheduledWeeklyDates || []),
        [scheduledWeeklyDates]
    );

    const dailyDatesSet = useMemo(
        () => new Set(scheduledDailyDates || []),
        [scheduledDailyDates]
    );

    // Custom DayButton component with scheduled dots
    const CustomDayButton = useCallback(
        (props) => {
            const { day, modifiers, children, ...buttonProps } = props;
            const date = day.date;

            if (!showScheduledDots) {
                return <button {...buttonProps}>{children}</button>;
            }

            const dateStr = moment(date).format('YYYY-MM-DD');
            const today = moment().startOf('day');
            const isFuture = moment(date).isAfter(today);

            const isWeeklyScheduled = isFuture && weeklyDatesSet.has(dateStr);
            const isDailyScheduled = isFuture && dailyDatesSet.has(dateStr);

            if (!isWeeklyScheduled && !isDailyScheduled) {
                return <button {...buttonProps}>{children}</button>;
            }

            const tooltipContent = <span>Top 100 update is scheduled</span>;

            return (
                <button {...buttonProps} style={{ position: 'relative' }}>
                    <Tooltip overlayContent={tooltipContent} placement="top" appendToBody>
                        <span>{children}</span>
                    </Tooltip>
                    {isWeeklyScheduled && <span style={scheduledDotStyles.weekly} />}
                    {isDailyScheduled && <span style={{ ...scheduledDotStyles.daily, right: isWeeklyScheduled ? '5px' : '1px' }} />}
                </button>
            );
        },
        [showScheduledDots, weeklyDatesSet, dailyDatesSet]
    );

    return (
        <DayPicker
            locale={enGB}
            firstDayOfWeek={1}
            className="Range"
            classNames={{
                root: 'DayPicker DayPicker-wrapper',
                month: 'DayPicker-Month',
                row: 'DayPicker-Week',
                head_row: 'DayPicker-WeekdaysRow',
                head_cell: 'DayPicker-Weekday',
                day: 'DayPicker-Day',
                caption: 'DayPicker-Caption',
                nav: 'DayPicker-NavBar',
                nav_button: 'DayPicker-NavButton',
                nav_button_previous: 'DayPicker-NavButton DayPicker-NavButton--prev',
                nav_button_next: 'DayPicker-NavButton DayPicker-NavButton--next',
            }}
            defaultMonth={getPreviousMonth()}
            startMonth={getFirstAvailableMonth()}
            endMonth={getEndMonth()}
            initialMonth={range.from || getFirstAvailableMonth()}
            numberOfMonths={2}
            onDayClick={handleDayClick}
            modifiers={getModifiers()}
            modifiersClassNames={{
                outside: 'DayPicker-Day--outside',
                disabled: 'DayPicker-Day--disabled',
                selected: 'DayPicker-Day--selected',
                from: 'DayPicker-Day--from',
                to: 'DayPicker-Day--to',
            }}
            components={{
                DayButton: CustomDayButton,
            }}
        />
    );
};

DateRangePicker.propTypes = {
    from: MomentType.isRequired,
    to: MomentType,
    start: MomentType,
    onChange: func.isRequired,
    scheduledWeeklyDates: arrayOf(string),
    scheduledDailyDates: arrayOf(string),
    showScheduledDots: bool,
};

DateRangePicker.defaultProps = {
    to: null,
    start: null,
    scheduledWeeklyDates: [],
    scheduledDailyDates: [],
    showScheduledDots: false,
};

export default DateRangePicker;
